import { createPopper } from '@popperjs/core'

// Shared open/close, focus and positioning for trigger + role="menu" popovers.
// Host provides refs `trigger` and `menu`, and `loading` / `disabled` props.
export default {
	data() {
		return {
			isOpen: false,
			popper: null
		}
	},
	beforeDestroy() {
		this.destroyPopper()
	},
	methods: {
		toggleMenu() {
			if (this.loading || this.disabled) return

			if (this.isOpen) {
				this.closeMenu()
				return
			}

			this.isOpen = true
			this.$emit('open')
			this.$nextTick(() => {
				this.createPopper()
				this.focusFirstOption()
			})
		},
		closeMenu(options = {}) {
			if (!this.isOpen) return

			this.isOpen = false
			this.destroyPopper()

			if (options.restoreFocus) {
				this.$nextTick(() => {
					this.focusTrigger()
				})
			}
		},
		closeOnEscape() {
			if (!this.isOpen) return

			// Modal closes on keyup, so swallow this Escape's release before it gets there.
			const swallowRelease = event => {
				if (event.key !== 'Escape') return

				event.stopPropagation()
				window.removeEventListener('keyup', swallowRelease, true)
			}
			window.addEventListener('keyup', swallowRelease, true)

			this.closeMenu({ restoreFocus: true })
		},
		handleFocusOut(event) {
			if (
				!this.isOpen ||
				(event.relatedTarget && this.$el.contains(event.relatedTarget))
			) {
				return
			}

			this.closeMenu()
		},
		focusTrigger() {
			const trigger = this.$refs.trigger?.$el || this.$refs.trigger
			trigger?.focus()
		},
		createPopper() {
			this.destroyPopper()
			const trigger = this.$refs.trigger?.$el || this.$refs.trigger

			if (!trigger || !this.$refs.menu) return

			this.popper = createPopper(trigger, this.$refs.menu, {
				placement: 'bottom-end',
				strategy: 'fixed',
				modifiers: [
					{ name: 'offset', options: { offset: [0, 8] } },
					{
						name: 'flip',
						options: { fallbackPlacements: ['top-end'] }
					},
					{ name: 'preventOverflow', options: { padding: 8 } }
				]
			})
		},
		destroyPopper() {
			if (!this.popper) return

			this.popper.destroy()
			this.popper = null
		},
		focusFirstOption() {
			this.menuOptions()[0]?.focus()
		},
		focusNextOption(direction) {
			const options = this.menuOptions()
			if (options.length === 0) return

			const currentIndex = options.indexOf(document.activeElement)
			const nextIndex =
				(currentIndex + direction + options.length) % options.length
			options[nextIndex].focus()
		},
		menuOptions() {
			return Array.from(
				this.$refs.menu?.querySelectorAll(
					'[role="menuitem"]:not([disabled])'
				) || []
			)
		}
	}
}
